import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
} from '@floating-ui/vue'
import { onClickOutside } from '@vueuse/core'

import type { CSSProperties } from 'vue'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import type { AssessmentTemplateListRow } from './interfaces'

import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons'

import { useSidebarPrivileges } from '@entities/viewer'

import { useAssessmentTemplatesStore } from '../assessment-templates/store'
import { useAssessmentTemplatesApi } from '../../api/assessment-templates/list.hook'
import { getAssessmentEditPath } from './assessment-template-options'
import { useAssessmentTemplateStatusChange } from './template-status-change.hook'
import { AssessmentTemplateStatus } from './enums'

interface UseAssessmentTemplateActionsOptions {
  template: ComputedRef<AssessmentTemplateListRow>
}

interface AssessmentTemplateActionsReturn {
  isOpen: Ref<boolean>
  containerRef: Ref<HTMLElement | null>
  floatingRef: Ref<HTMLElement | null>
  floatingStyles: Ref<CSSProperties>
  triggerIcon: IconDefinition
  canManage: ComputedRef<boolean>
  /** Archived rows offer "Unarchive", every other row offers "Archive". */
  refIsArchived: ComputedRef<boolean>
  /** A status change is in flight for THIS row — the menu items are disabled meanwhile. */
  refIsChangingStatus: Ref<boolean>
  toggle: () => void
  openEdit: () => Promise<void>
  openDuplicate: () => void
  openDelete: () => void
  archive: () => Promise<void>
  unarchive: () => Promise<void>
}

export const useAssessmentTemplateActions = (
  { template }: UseAssessmentTemplateActionsOptions,
): AssessmentTemplateActionsReturn => {
  const router = useRouter()
  const store = useAssessmentTemplatesStore()
  const { canManageAssessmentTemplates } = useSidebarPrivileges()

  const isOpen = ref(false)
  const containerRef = ref<HTMLElement | null>(null)
  const floatingRef = ref<HTMLElement | null>(null)

  const { floatingStyles } = useFloating(containerRef, floatingRef, {
    placement: 'bottom-end',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
    ],
  })

  const { changeStatus, refIsChanging: refIsChangingStatus } = useAssessmentTemplateStatusChange()

  const canManage = computed<boolean>(() => canManageAssessmentTemplates.value)
  const refIsArchived = computed<boolean>(() => template.value.status === AssessmentTemplateStatus.Archived)

  const toggle = (): void => {
    isOpen.value = !isOpen.value
  }

  const openEdit = async (): Promise<void> => {
    isOpen.value = false
    await router.push(getAssessmentEditPath(template.value.id))
  }

  const openDuplicate = (): void => {
    isOpen.value = false
    store.setTemplateToDuplicate(template.value)
  }

  const openDelete = (): void => {
    isOpen.value = false
    store.setTemplateToDelete(template.value)
  }

  /**
   * Refetched rather than patched in place: the index is sorted by status first (Draft → Published
   * → Archived), so the row moves, and `updated_at` is server-set. Publish is NOT offered here — it
   * needs the document validated, and the row carries no `data`; the editor owns that gate.
   */
  const setStatus = async (status: AssessmentTemplateStatus): Promise<void> => {
    isOpen.value = false

    const updated = await changeStatus({ templateId: template.value.id, status })

    if (!updated) return

    await useAssessmentTemplatesApi()
  }

  const archive = async (): Promise<void> => {
    await setStatus(AssessmentTemplateStatus.Archived)
  }

  /** Back to `DRAFT`, never straight to `PUBLISHED`: what was hidden must be looked at before
   *  tutors see it again. */
  const unarchive = async (): Promise<void> => {
    await setStatus(AssessmentTemplateStatus.Draft)
  }

  onClickOutside(containerRef, () => {
    isOpen.value = false
  })

  return {
    isOpen,
    containerRef,
    floatingRef,
    floatingStyles,
    triggerIcon: faEllipsisVertical,
    canManage,
    refIsArchived,
    refIsChangingStatus,
    toggle,
    openEdit,
    openDuplicate,
    openDelete,
    archive,
    unarchive,
  }
}
